import React, { createContext, useContext, useCallback, useEffect, useMemo, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { loadB2BPermissions } from '../redux/actions/b2bPermissionsActions';
import { normalizeModules, buildPages } from '../routes/pages';
import { userIsLoggedIn } from '../pages/Login/Session/Session';
import { parseScopeType } from '../utils/scopeType';

const B2BPermissionsContext = createContext(null);

// B2B permissions backend. The endpoint
// GET /admin/modules/permissions/{personId} returns { success, message, scope, modules }.
// The user's scope lives at scope[scope.scope_type].companies (internal OR external).
// Set to `false` to disable scope-based restrictions (e.g. for local dev when the
// permissions service is unavailable); when disabled, no company scope is loaded.
const PERMISSIONS_ENDPOINT_ENABLED = true;

// Array vacío con identidad ESTABLE. Devolver `[]` literal en cada render crea una
// referencia nueva cada vez, lo que re-dispara los useEffect que dependen de
// `dynamicPages`/`filteredPages` (p. ej. el del Sidebar) y provoca un bucle de
// history.replace ("Too many attempts to navigate"). Reutilizar esta constante
// mantiene la referencia estable mientras no haya módulos.
const EMPTY_PAGES = [];

// Normalize a company name for comparison. The catalog `display_name` (e.g. "Auric")
// and the scope `company_name` (e.g. "AURIC") can differ in case/spacing, so we
// compare them trimmed and upper-cased.
const normalizeCompanyName = (name) => (name || '').toString().trim().toUpperCase();

export function B2BPermissionsProvider({ children }) {
    const permissions = useSelector((state) => state.b2bPermissionsReducer);
    const personId = useSelector((state) => state.userReducer?.user?.person_id);
    const dispatch = useDispatch();

    const dispatchLoadPermissions = useCallback(
        (personId) => dispatch(loadB2BPermissions(personId)),
        [dispatch]
    );

    // Guarda el último personId para el que YA se disparó la carga de permisos.
    // Sin esto, si el efecto se re-ejecutara (p. ej. el fetch falla y algo vuelve
    // a montar/renderizar), se re-dispararía loadB2BPermissions en bucle: cada
    // intento hace START (loading:true) → ERROR (loading:false), oscilando el
    // estado y, con él, la navegación ("Too many attempts to navigate"). Disparar
    // una sola vez por personId corta ese bucle incluso cuando el backend falla.
    const loadedForPersonId = useRef(null);

    // Auto-load the user's B2B permissions once they are logged in. This wires the
    // scope (the companies the user has configured) that the company selectors rely
    // on. Gated behind PERMISSIONS_ENDPOINT_ENABLED. Fires automatically when a
    // person_id is present, pero SOLO una vez por personId (ver loadedForPersonId).
    useEffect(() => {
        if (PERMISSIONS_ENDPOINT_ENABLED && personId && loadedForPersonId.current !== personId) {
            loadedForPersonId.current = personId;
            dispatchLoadPermissions(personId);
        }
    }, [personId, dispatchLoadPermissions]);

    // Clave de contenido ESTABLE para los módulos del backend. `permissions` es un
    // objeto redux nuevo en cada acción (incluso loading:true→false sin cambios de
    // módulos), y buildPages() siempre crea arrays nuevos. Serializar los módulos
    // nos da una dependencia que solo cambia cuando el CONTENIDO cambia de verdad,
    // evitando que dynamicPages mute de identidad en cada render.
    const modulesKey = JSON.stringify(permissions.modules || []);

    // `dynamicPages` memoizado por contenido (modulesKey) + estado de error/loading.
    // Mantener su identidad estable es lo que corta el bucle de navegación del
    // Sidebar (useEffect [currentPath, filteredPages] → history.replace).
    const dynamicPages = useMemo(() => {
        // NO existe un menú de fallback: exponer un árbol estático ante un fallo o
        // durante la carga abría acceso a módulos sin verificar permisos. El árbol
        // vacío (EMPTY_PAGES, referencia estable) + el loader de Routes.jsx cubren
        // esos casos de forma segura (fail-closed).
        if (permissions.error) {
            // Fallo del endpoint de permisos: no exponer módulos (fail-closed).
            return EMPTY_PAGES;
        }
        const normalized = normalizeModules(permissions.modules || []);
        if (normalized.length > 0) {
            // Permisos verificados: usar el árbol real del backend.
            return buildPages(normalized);
        }
        // Sin módulos (carga en curso, usuario sin permisos, o estado inicial):
        // array vacío estable. Routes.jsx usa `permissionsLoading` para decidir
        // entre loader (cargando) y redirect (sin acceso).
        return EMPTY_PAGES;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [modulesKey, permissions.error]);

    const value = useMemo(() => {
        // El scope puede tener uno o AMBOS tipos activos. `scope_type` llega como
        // string CSV ("internal", "external" o "internal, external"). Se parsea a un
        // arreglo de tipos activos y se COMBINAN las empresas de cada caja activa.
        // Mismo contrato que consume Roles.jsx.
        const scope = permissions.scope || {};
        const activeTypes = parseScopeType(scope.scope_type);

        // Tipo EFECTIVO del módulo B2B. Regla de prioridad: si el alcance incluye
        // EXTERNAL, el tipo efectivo es "external" (las empresas de internal se
        // ignoran, incluso en el caso combinado internal + external). Si el alcance
        // es solo internal, el efectivo es "internal". Resumen:
        //   - internal + external → "external"
        //   - solo external       → "external"
        //   - solo internal       → "internal"
        const effectiveType = activeTypes.includes('external')
            ? 'external'
            : (activeTypes[0] || null);

        // `scopeType` se expone para los consumidores que lo reenvían al backend
        // (p. ej. useStudentSearch). Refleja el tipo EFECTIVO para ser consistente
        // con las empresas enviadas (empresa_tag): si el alcance incluye external,
        // las empresas son las de external, así que el scope_type reportado también
        // es "external".
        const scopeType = effectiveType;

        // Empresas EFECTIVAS: las del tipo efectivo resuelto arriba.
        const activeCompanies = effectiveType
            ? (scope[effectiveType]?.companies || [])
            : [];

        // The companies the logged-in user has configured. External companies carry
        // { company_id, company_name }; internal ones { company_id, company_name, campuses }.
        const assignedCompanyId = activeCompanies.map((c) => c.company_id);
        const assignedCompanyName = activeCompanies.map((c) => c.company_name);

        // List for selectors: { company_id, company_name } for each effective company.
        const assignedCompanies = activeCompanies.map((c) => ({
            company_id: c.company_id,
            company_name: c.company_name,
        }));

        // Autoselección de empresa para los selectores B2B: cuando las empresas
        // EFECTIVAS (ya resueltas por la regla de prioridad de arriba) son
        // EXACTAMENTE UNA, se precarga y preselecciona en todos los catálogos. Esto
        // cubre: solo internal con 1 empresa, solo external con 1 empresa, y también
        // internal + external cuyo external tiene 1 sola empresa. En cualquier otro
        // caso (varias empresas) el valor es null y los selectores muestran todas.
        const autoSelectCompany =
            assignedCompanies.length === 1 ? assignedCompanies[0] : null;

        // Pre-compute the normalized set of assigned names for fast membership tests.
        const assignedNameSet = new Set(assignedCompanyName.map(normalizeCompanyName));

        // Restrict a company catalog (from getCatalogCompanies type=company) to only
        // the companies configured for the user. Matches catalog `display_name`
        // against the scope `company_name`, both normalized (trim + upper-case).
        // If the user has no configured companies, returns an empty list — there is
        // no "see everything" fallback by design.
        const filterCompaniesByScope = (catalogCompanies = []) =>
            (catalogCompanies || []).filter((company) =>
                assignedNameSet.has(normalizeCompanyName(company.display_name ?? company.label))
            );

        // --- Estado de carga de permisos (fail-closed / seguridad) ---
        // `dynamicPages` ya está calculado arriba con identidad estable. Aquí solo
        // derivamos la señal de carga para que Routes.jsx distinga "cargando" de
        // "sin acceso".
        //
        // ¿Ya se disparó al menos un intento de carga? El estado inicial del
        // reducer es { loading:false, modules:[], scope:null, error:null }. En
        // cuanto arranca el fetch pasa a loading:true, y al terminar queda scope
        // o error poblado. Si cualquiera de esos cambió, ya hubo un intento.
        const hasAttemptedLoad =
            permissions.loading === true ||
            permissions.scope !== null ||
            permissions.error !== null;

        // Señal para que el ruteo distinga "cargando permisos" de "sin acceso".
        // En esos casos Routes.jsx muestra un loader en vez de redirigir, lo que
        // evita el bucle de redirección a login.
        //
        // Caso crítico (F5 estando logueado): la cookie dice "logueado" al instante,
        // pero Redux arranca vacío y App.js rehidrata `user` (y por tanto personId)
        // en un useEffect, UN render después. En ese primer render personId es
        // undefined. Si no lo tratáramos como "cargando", Routes evaluaría con el
        // árbol vacío y redirigiría a login antes de que lleguen los permisos.
        //
        // Por eso: si hay sesión por cookie pero aún no hay personId en Redux,
        // seguimos en estado de carga (esperando la rehidratación).
        const loggedInByCookie = userIsLoggedIn();
        const awaitingUserHydration = loggedInByCookie && !personId;
        const willLoadPermissions = PERMISSIONS_ENDPOINT_ENABLED && !!personId;
        const permissionsLoading =
            (PERMISSIONS_ENDPOINT_ENABLED && awaitingUserHydration) ||
            (willLoadPermissions && (permissions.loading === true || !hasAttemptedLoad));

        return {
            ...permissions,
            permissionsLoading,
            scopeType,
            // Tipos de alcance CRUDOS configurados por el usuario (['internal'],
            // ['external'] o ['internal','external']). A diferencia de `scopeType`
            // (que refleja el tipo EFECTIVO para empresas/backend), esto conserva lo
            // que el usuario tiene asignado, para decisiones de UI (p. ej. mostrar el
            // botón "+ Nueva Empresa" cuando tiene internal, aunque también external).
            scopeTypes: activeTypes,
            assignedCompanyId,
            assignedCompanyName,
            assignedCompanies,
            autoSelectCompany,
            filterCompaniesByScope,
            dynamicPages,
            loadPermissions: dispatchLoadPermissions,
        };
    }, [permissions, personId, dynamicPages, dispatchLoadPermissions]);

    return (
        <B2BPermissionsContext.Provider value={value}>
            {children}
        </B2BPermissionsContext.Provider>
    );
}

export function useB2BPermissions() {
    const context = useContext(B2BPermissionsContext);
    if (!context) {
        throw new Error(
            'useB2BPermissions must be used within a B2BPermissionsProvider'
        );
    }
    return context;
}

export default B2BPermissionsContext;
